import {
  ArrowLeft,
  ArrowDown,
  ArrowUp,
  Calendar,
  Crown,
  MapPin,
  SlidersHorizontal,
  Swords,
  Trophy,
} from "lucide-react";
import { PhoneMock } from "../PhoneMock";

/**
 * App preview: the Compete Rankings leaderboard.
 *
 * Mirrors apps/legacy-mobile/src/pages/CompeteRankings.jsx - filter
 * pill row (matches the Open Runs / Browse Courts pattern), a proper
 * top-3 podium under the title, and a ranked team list below. Each
 * row shows position, movement arrow (up/down/same), crest, name,
 * earned badges (Rising, Most Played, Undefeated), and W–L record.
 * The "YOU" row is highlighted in volt.
 */
export function RankingsMock({ className }: { className?: string }) {
  return (
    <PhoneMock className={className}>
      {/* Status bar */}
      <div className="flex items-center justify-between px-6 pt-4 pb-1 text-[10px] font-semibold">
        <span>9:41</span>
        <span className="flex items-center gap-1 text-[9px]">● ● ●</span>
      </div>

      {/* Back link */}
      <div className="px-4 pt-3 pb-1">
        <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-[#8899A6]">
          <ArrowLeft className="h-3 w-3" />
          Compete
        </div>
      </div>

      {/* Title */}
      <div className="px-4 pb-2">
        <h1 className="text-[20px] font-black leading-tight tracking-tight text-[#F5F5DC]">
          Rank<span className="text-[#FF6200]">ings</span>
        </h1>
        <p className="mt-0.5 text-[10px] text-[#8899A6]">
          Top teams in the city
        </p>
      </div>

      {/* Filter pill row - matches Open Runs / Browse Courts style */}
      <div className="flex items-center gap-1.5 overflow-hidden px-4 pb-2.5">
        <SlidersHorizontal className="h-3 w-3 shrink-0 text-[#FF6200]" />
        <FilterPill icon={<MapPin className="h-2.5 w-2.5" />} label="London" />
        <FilterPill
          icon={<Calendar className="h-2.5 w-2.5" />}
          label="Monthly"
        />
        <FilterPill icon={<Swords className="h-2.5 w-2.5" />} label="5v5" />
      </div>

      {/* Top 3 podium */}
      <div className="mx-4 mb-2 rounded-2xl border border-[#1e2832] bg-gradient-to-b from-[#131a20] to-[#0d1117] p-2.5">
        <div className="mb-1.5 flex items-center gap-1.5">
          <Trophy
            className="h-3 w-3 text-[#FFD700]"
            fill="#FFD700"
            strokeWidth={1.5}
          />
          <span className="text-[9px] font-bold uppercase tracking-wider text-[#FFD700]">
            Top of the city
          </span>
        </div>
        <div className="flex items-end justify-center gap-1.5">
          <PodiumBlock
            position={2}
            initials="BK"
            name="Brixton Kings"
            record="12–3"
            height={50}
            color="#A3B5C6"
          />
          <PodiumBlock
            position={1}
            initials="SA"
            name="Shoreditch Allstars"
            record="14–1"
            height={68}
            color="#FFD700"
            crown
          />
          <PodiumBlock
            position={3}
            initials="CH"
            name="Camden Heat"
            record="11–4"
            height={36}
            color="#CD7F32"
          />
        </div>
      </div>

      {/* Ranked list - positions 4+ */}
      <div className="flex flex-col gap-1 px-4">
        <RankRow
          position={4}
          movement="down"
          name="Peckham Pros"
          record="10–5"
          badge={{
            label: "Most Played",
            color: "#60a5fa",
            bg: "rgba(96,165,250,0.14)",
          }}
        />
        <RankRow
          position={5}
          movement="up"
          name="Hackney Reign"
          record="9–5"
          badge={{ label: "Rising", color: "#a78bfa", bg: "rgba(167,139,250,0.14)" }}
          you
        />
        <RankRow
          position={6}
          movement="same"
          name="Westside Warriors"
          record="8–6"
        />
      </div>
    </PhoneMock>
  );
}

function FilterPill({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <div className="flex shrink-0 items-center gap-1 rounded-full border border-[#FF6200] bg-[#FF6200] px-2 py-0.5 text-[9px] font-bold text-[#F5F5DC]">
      {icon}
      <span>{label}</span>
    </div>
  );
}

function PodiumBlock({
  position,
  initials,
  name,
  record,
  height,
  color,
  crown = false,
}: {
  position: number;
  initials: string;
  name: string;
  record: string;
  height: number;
  color: string;
  crown?: boolean;
}) {
  return (
    <div className="flex w-[70px] flex-col items-center">
      {/* Crest + crown */}
      <div className="relative mb-0.5">
        {crown && (
          <Crown
            className="absolute -top-3 left-1/2 h-3 w-3 -translate-x-1/2 text-[#FFD700]"
            fill="#FFD700"
            strokeWidth={1.5}
          />
        )}
        <div
          className="flex h-7 w-7 items-center justify-center rounded-md text-[9px] font-black"
          style={{
            background: "#0d1117",
            color: color,
            border: `1.5px solid ${color}`,
          }}
        >
          {initials}
        </div>
      </div>

      {/* Name + record */}
      <p className="truncate text-center text-[8.5px] font-black leading-tight text-[#F5F5DC]">
        {name}
      </p>
      <p className="text-center text-[7.5px] font-semibold text-[#8899A6]">
        {record}
      </p>

      {/* Block */}
      <div
        className="mt-1 flex w-full items-start justify-center rounded-t-md pt-1"
        style={{
          height,
          background: `linear-gradient(180deg, ${color}30 0%, ${color}10 100%)`,
          borderTop: `2px solid ${color}`,
        }}
      >
        <span
          className="text-[11px] font-black"
          style={{ color, textShadow: "0 1px 2px rgba(0,0,0,0.4)" }}
        >
          {position}
        </span>
      </div>
    </div>
  );
}

function RankRow({
  position,
  movement,
  name,
  record,
  badge,
  you = false,
}: {
  position: number;
  movement: "up" | "down" | "same";
  name: string;
  record: string;
  badge?: { label: string; color: string; bg: string };
  you?: boolean;
}) {
  return (
    <div
      className="flex items-center gap-2 rounded-xl border px-2.5 py-2"
      style={{
        background: you ? "rgba(255,98,0,0.08)" : "#131a20",
        borderColor: you ? "rgba(255,98,0,0.4)" : "#1e2832",
      }}
    >
      {/* Position + movement */}
      <div className="flex w-6 shrink-0 flex-col items-center">
        <span className="text-[12px] font-black leading-none text-[#F5F5DC]">
          {position}
        </span>
        {movement === "up" && (
          <ArrowUp className="mt-0.5 h-2 w-2 text-[#4ade80]" strokeWidth={3} />
        )}
        {movement === "down" && (
          <ArrowDown
            className="mt-0.5 h-2 w-2 text-[#f87171]"
            strokeWidth={3}
          />
        )}
        {movement === "same" && (
          <span className="mt-0.5 h-0.5 w-2 rounded-full bg-[#4a5568]" />
        )}
      </div>

      {/* Crest */}
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#1e2832] text-[9px] font-black text-[#F5F5DC]">
        {name
          .split(" ")
          .map((w) => w[0])
          .slice(0, 2)
          .join("")}
      </div>

      {/* Name + badge */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-[10px] font-bold text-[#F5F5DC]">
            {name}
          </span>
          {you && (
            <span className="shrink-0 rounded-sm bg-[#FF6200] px-1 py-px text-[7px] font-black text-[#0d1117]">
              YOU
            </span>
          )}
        </div>
        {badge && (
          <span
            className="mt-0.5 inline-block rounded-full px-1.5 py-px text-[7px] font-bold"
            style={{ background: badge.bg, color: badge.color }}
          >
            {badge.label}
          </span>
        )}
      </div>

      {/* Record */}
      <div className="flex shrink-0 items-center gap-0.5">
        <Trophy className="h-2.5 w-2.5 text-[#4a5568]" />
        <span className="text-[10px] font-bold text-[#8899A6]">{record}</span>
      </div>
    </div>
  );
}
